import { useEffect, useState } from 'react';
import { fetchCallLogs } from '../api';
import { Megaphone, CircleCheck, CircleX, Clock3, ExternalLink } from 'lucide-react';

type CallLogsTypeFilter = 'all' | 'outgoing' | 'incoming' | 'missed';
type CallLogsDateFilter = 'today' | 'yesterday' | 'last7' | 'last30' | 'range';

const TYPE_FILTERS: { id: CallLogsTypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'outgoing', label: 'Outgoing' },
  { id: 'incoming', label: 'Incoming' },
  { id: 'missed', label: 'Missed' },
];

const AGENT_DATE_FILTERS: { id: CallLogsDateFilter; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
];

const PRIVILEGED_DATE_FILTERS: { id: CallLogsDateFilter; label: string }[] = [
  ...AGENT_DATE_FILTERS,
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
];

const PAGE_SIZE = 10;

export function isConnectedCall(callLog: any) {
  if (callLog.technical_state === 'reconciliation_failed') return false;
  const callStatus = String(callLog.call_status || callLog.raw_call_status || callLog.status || '').toLowerCase();
  return callStatus === 'completed';
}

function isMissedOrNotConnected(callLog: any) {
  if (callLog.technical_state === 'reconciliation_failed') return false;
  const callStatus = String(callLog.call_status || callLog.raw_call_status || callLog.status || '').toLowerCase();
  const callType = String(callLog.call_type || callLog.raw_call_type || '').toLowerCase();
  return (
    callType === 'missed' ||
    String(callLog.missed_reason || '').toLowerCase() === 'queue_expired_without_accept' ||
    ['missed', 'expired', 'no_answer', 'not_answered', 'busy', 'switched_off', 'not_connected'].includes(callStatus)
  );
}

export function displayCallType(callLog: any) {
  if (callLog.technical_state === 'reconciliation_failed') return 'needs_check';
  const callStatus = String(callLog.call_status || callLog.raw_call_status || '').toLowerCase();
  if (callStatus === 'completed') return 'connected';
  if (callStatus === 'not_connected') return 'not_connected';
  if (callStatus === 'technical_failure') return 'technical_failure';
  if (callStatus === 'initiated') return 'initiated';
  return String(callLog.call_type || callLog.raw_call_type || '').toLowerCase();
}

function CampaignIcon({ campaignKey }: { campaignKey: string }) {
  const key = String(campaignKey).toLowerCase();

  switch (key) {
    case 'whatsapp':
      return (
        <svg className="h-5 w-5 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.358-.214-3.741.982.999-3.648-.234-.375a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.134 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.68 1.448h.006c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
        </svg>
      );
    case 'facebook_ads_campaign':
      return (
        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H6v4h4v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      );
    case 'manual':
      return (
        <svg className="h-5 w-5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
        </svg>
      );
    case 'website_form':
      return (
        <svg className="h-5 w-5 text-cyan-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      );
    case 'neodove_third_party':
      return (
        <svg className="h-5 w-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
        </svg>
      );
    case 'feedback':
      return (
        <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'answered_calls':
      return (
        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case 'ivr':
      return (
        <svg className="h-5 w-5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
        </svg>
      );
    case 'missed_calls':
      return (
        <svg className="h-5 w-5 text-rose-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.68 13.31a16 16 0 0 0 3.41 2.2l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7a2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91" />
          <line x1="23" y1="1" x2="1" y2="23" />
        </svg>
      );
    default:
      return (
        <svg className="h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      );
  }
}

export default function CallLogsTab({
  active,
  onSelect,
  privileged,
  recentIvrCalls = [],
}: {
  active: boolean;
  onSelect: (id: string) => void;
  privileged: boolean;
  recentIvrCalls?: any[];
}) {
  const [typeFilter, setTypeFilter] = useState<CallLogsTypeFilter>('all');
  const [dateFilter, setDateFilter] = useState<CallLogsDateFilter>('today');
  const [search, setSearch] = useState('');
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const dateFilters = privileged ? PRIVILEGED_DATE_FILTERS : AGENT_DATE_FILTERS;

  const loadCallLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchCallLogs({
        type: typeFilter,
        date: dateFilter,
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setCallLogs(response.call_logs || []);
      setHasMore(Boolean(response.has_more));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load call logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    loadCallLogs();
  }, [active, typeFilter, dateFilter, page, search]);

  const handleTypeFilterChange = (newType: CallLogsTypeFilter) => {
    setTypeFilter(newType);
    setPage(1);
  };

  const handleDateFilterChange = (newDate: CallLogsDateFilter) => {
    setDateFilter(newDate);
    setPage(1);
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setPage(page + 1);
    }
  };

  const handleView = (callLog: any) => {
    if (callLog.lead_id) {
      onSelect(callLog.lead_id);
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const isToday = new Date().toDateString() === date.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds === 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const recentCallTime = (call: any) => formatDateTime(call.finalized_at || call.created_at);

  return (
    <div className="space-y-4 px-3 py-3 pb-32">
      {/* Header */}
      <div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Call Logs</h1>
          <p className="text-sm text-slate-500">View call history and recordings</p>
        </div>
      </div>

      {!privileged && recentIvrCalls.some((call) => call.needs_disposition) && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-semibold text-amber-950">Needs Disposition</h2>
            <span className="text-xs text-amber-800">Recent completed IVR calls</span>
          </div>
          <div className="space-y-2">
            {recentIvrCalls.filter((call) => call.needs_disposition).map((call) => (
              <button
                key={call.call_uuid || `${call.lead_id}-${call.created_at}`}
                type="button"
                onClick={() => handleView(call)}
                disabled={!call.lead_id}
                className="flex w-full items-center justify-between rounded-md border border-amber-200 bg-white px-3 py-2 text-left hover:bg-amber-100 disabled:cursor-default disabled:opacity-70"
              >
                <span>
                  <span className="block font-medium text-slate-900">{call.patient_name || 'Unknown Patient'}</span>
                  <span className="block text-sm text-slate-600">{call.lead_phone || call.customer_phone || 'Phone unavailable'}</span>
                  <span className="block text-xs text-slate-500">
                    {recentCallTime(call)} · {formatDuration(Number(call.duration_seconds || 0))}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-semibold text-white">
                  Dispose Now <ExternalLink className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!privileged && recentIvrCalls.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-3">
          <h2 className="mb-2 font-semibold text-slate-900">Recent IVR Calls</h2>
          <div className="space-y-1">
            {recentIvrCalls.map((call) => (
              <button
                key={`recent-${call.call_uuid || `${call.lead_id}-${call.created_at}`}`}
                type="button"
                onClick={() => handleView(call)}
                disabled={!call.lead_id}
                className="grid w-full grid-cols-[auto_1fr_auto_auto] gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-default disabled:opacity-70"
              >
                <span className="text-slate-500">{recentCallTime(call)}</span>
                <span className="font-medium text-slate-900">{call.patient_name || 'Unknown Patient'}</span>
                <span className="text-slate-600">{call.lead_phone || call.customer_phone || '-'}</span>
                <span className="text-slate-500">{call.call_status || call.status}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search name or phone"
          aria-label="Search call logs by name or phone"
          className="min-w-56 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        {/* Date Filter */}
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {dateFilters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleDateFilterChange(filter.id)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                dateFilter === filter.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {TYPE_FILTERS.map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleTypeFilterChange(filter.id)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                typeFilter === filter.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Legend:</span>
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800">
          <CircleCheck className="h-4 w-4 text-emerald-600" />
          Connected call
        </span>
        <span className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-800">
          <CircleX className="h-4 w-4 text-rose-600" />
          Missed / not connected
        </span>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <div>{error}</div>
          <button
            onClick={loadCallLogs}
            className="mt-2 rounded-md bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-3 py-4">
          <div className="text-center text-sm text-slate-500">Loading call logs...</div>
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-16 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && callLogs.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-4xl">📞</div>
          <div className="mt-2 text-sm text-slate-500">No records found</div>
        </div>
      )}

      {/* Table */}
      {callLogs.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600 w-28"><Megaphone className="h-4 w-4" /></th>
                <th className="px-4 py-2 text-left font-medium text-slate-600 w-28">Name</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Mobile Number</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Call Details</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {callLogs.map((callLog) => (
                <tr
                  key={callLog.id}
                  onClick={() => handleView(callLog)}
                  className={`cursor-pointer transition-colors ${
                    isConnectedCall(callLog)
                      ? 'bg-emerald-50/80 hover:bg-emerald-100'
                      : isMissedOrNotConnected(callLog)
                        ? 'bg-orange-50/80 hover:bg-orange-100'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <td className="px-4 py-3 w-28"><CampaignIcon campaignKey={callLog.campaign_key} /></td>
                  <td className="px-4 py-3 font-medium text-slate-900 w-28">
                    <div>{callLog.name}</div>
                    {String(callLog.call_origin || '').toLowerCase() === 'ivr_agent_queue' && (
                      <span className="mt-1 inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-800">
                        IVR Incoming
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{callLog.phone}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div className="text-xs">
                      <div>{formatDateTime(callLog.created_at)}</div>
                      {callLog.duration_seconds > 0 && (
                        <div className="text-slate-500">Duration: {formatDuration(callLog.duration_seconds)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const status = displayCallType(callLog);
                      return status === 'connected' ? (
                        <CircleCheck className="h-4 w-4 text-emerald-600" aria-label="Connected" />
                      ) : status === 'technical_failure' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                          <CircleX className="h-4 w-4 text-slate-500" aria-hidden="true" />
                          Technical failure — not the patient
                        </span>
                      ) : status === 'needs_check' || !isMissedOrNotConnected(callLog) ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <Clock3 className="h-4 w-4" aria-hidden="true" />
                          {status === 'needs_check' ? 'Outcome needs checking' : 'Awaiting outcome'}
                        </span>
                      ) : (
                        <CircleX className="h-4 w-4 text-rose-600" aria-label="Not Connected" />
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {callLogs.length > 0 && (
        <div className="sticky bottom-20 z-10 flex items-center justify-between border-t border-slate-200 bg-slate-50/95 py-3 backdrop-blur">
          <div className="text-sm text-slate-500">
            Page {page}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={page === 1}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={!hasMore}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
